/**
 * Client-side verification of static device-key attestation.
 *
 * A wrap must never target a device whose ECDH/KEM keys are not bound to the
 * identity signing key. Missing or invalid `staticKeyAttestation` fails closed.
 */

import {
  fromBase64,
  toBase64,
  signDeviceStaticKeyAttestation,
  verifyDeviceStaticKeyAttestation,
  type CryptoProfile,
} from '@adieuu/crypto';
import type { PublicDevice } from '@adieuu/shared';

export function deviceStaticAttestationHolds(
  signingPublicKeyB64: string,
  device: PublicDevice,
  profile: CryptoProfile,
  pqSigningPublicKeyB64?: string,
): boolean {
  if (!device.staticKeyAttestation || !device.ecdhPublicKey) return false;
  try {
    const signingPub = fromBase64(signingPublicKeyB64);
    const ecdh = fromBase64(device.ecdhPublicKey);
    const kem =
      device.kemPublicKey != null && device.kemPublicKey.length > 0
        ? fromBase64(device.kemPublicKey)
        : new Uint8Array(0);
    const signature = fromBase64(device.staticKeyAttestation);
    return verifyDeviceStaticKeyAttestation(
      signingPub,
      signature,
      {
        profile,
        deviceId: device.deviceId,
        ecdhPublicKey: ecdh,
        kemPublicKey: kem,
      },
      pqSigningPublicKeyB64 ? fromBase64(pqSigningPublicKeyB64) : undefined,
    );
  } catch {
    return false;
  }
}

/** Test/helper: attach a valid static-key attestation to a public device. */
export function attachDeviceStaticAttestation(
  signingPrivateKey: Uint8Array,
  device: PublicDevice,
  profile: CryptoProfile = 'default',
  pqSigningPrivateKey?: Uint8Array,
): PublicDevice {
  const ecdh = fromBase64(device.ecdhPublicKey);
  const kem =
    device.kemPublicKey != null && device.kemPublicKey.length > 0
      ? fromBase64(device.kemPublicKey)
      : new Uint8Array(0);
  const signature = signDeviceStaticKeyAttestation(signingPrivateKey, {
    profile,
    deviceId: device.deviceId,
    ecdhPublicKey: ecdh,
    kemPublicKey: kem,
  }, pqSigningPrivateKey);
  return { ...device, staticKeyAttestation: toBase64(signature) };
}
